import { ICommonSettings } from "@kuika/kuika-cl-model";
import { IWhatsappBusinessSettings } from "@kuika/kuika-cl-model/src/models/common-config";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import * as React from "react";
import { IWithSDProps } from "../../../../../shared/hoc/screen-design-consumer";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

export interface IWhatsappBusinessSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingConfig?: IWhatsappBusinessSettings;
}

export interface IWhatsappBusinessSettingsProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingWhatsappBusinessId?: string;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => React.ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class WhatsappBusinessSettings extends React.PureComponent<
  IWhatsappBusinessSettingsProps,
  IWhatsappBusinessSettingsState
> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IWhatsappBusinessSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle"
    };
  }

  componentDidMount() {
    this.loadSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IWhatsappBusinessSettingsProps & IWithSDProps) {
    if (prevProps.editingWhatsappBusinessId !== this.props.editingWhatsappBusinessId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    }
  }

  loadSetting = () => {
    if (!this.props.editingWhatsappBusinessId) return;

    const editingConfig = this.props.commonSettings.whatsappBusinessSettings?.find(
      (x) => x.id === this.props.editingWhatsappBusinessId && !x.isGlobalCommonConfigOnProjectConfig
    );

    if (!editingConfig) return;
    this.setState({ editingConfig }, () => {
      this.formRef.current?.resetFields();
      this.formRef.current?.setFieldsValue(editingConfig);
    });
  };

  existingNameValidator = async (_rule, value) => {
    const whatsappBusinessSettings = this.props.commonSettings?.whatsappBusinessSettings;
    if (!whatsappBusinessSettings) return;

    const isSettingAlreadyExist = whatsappBusinessSettings.some(
      (x) => x.name === value && x.id !== this.props.editingWhatsappBusinessId && !x.isGlobalCommonConfigOnProjectConfig
    );

    if (isSettingAlreadyExist) throw new Error("Whatsapp Business Name is already exist.");
  };

  onFinish = (data: any) => {
    this.saveStatus.start();
    let obj: IWhatsappBusinessSettings;
    obj = {
      id: Guid.create().toString(),
      name: data.name,
      accessToken: data.accessToken?.trim(),
      phoneNumberId: data.phoneNumberId?.trim(),
      versionNumber: data.versionNumber?.trim(),
      appId: data.appId?.trim(),
      appSecret: data.appSecret?.trim(),
      businessAccountId: data.businessAccountId?.trim()
    };
    if (this.props.editingWhatsappBusinessId) {
      obj.id = this.props.editingWhatsappBusinessId;
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "whatsappBusinessSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "whatsappBusinessSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deleteSetting = () => {
    if (!this.props.editingWhatsappBusinessId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingWhatsappBusinessId || "",
      configKey: "whatsappBusinessSettings",

      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`Whatsapp Business setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  render() {
    // appsettings_whatsappbusinesssettingpop_up_description
    // appsettings_whatsappbusinesssettingpop_up_name
    // appsettings_whatsappbusinesssettingpop_up_accountid
    // appsettings_whatsappbusinesssettingpop_up_appid
    // appsettings_whatsappbusinesssettingpop_up_appsecret
    // appsettings_whatsappbusinesssettingpop_up_accesstoken
    // appsettings_whatsappbusinesssettingpop_up_phonenumberid
    // appsettings_whatsappbusinesssettingpop_up_versionnumber

    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Whatsapp Business setting"
        secondHeader="Whatsapp Business setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingWhatsappBusinessId ? this.deleteSetting : undefined}
            deleteTestId="whatsappBusinessSettingDeleteButton"
            submitTestId="whatsappBusinessSettingCreateButton"
            submitLabel={this.props.editingWhatsappBusinessId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configWhatsappBusinessForm"
          />
        }
      >
        <Form
          id="configWhatsappBusinessForm"
          name="whatsappBusinessSettings"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{ height: "100%" }}
        >
          <ConfigModalBody
            description={this.props.getSectionDescription("appsettings_whatsappbusinesssettingpop_up_description")}
          >
            <ConfigField label="Name" required tooltipContentKey="appsettings_whatsappbusinesssettingpop_up_name">
              <Form.Item
                name="name"
                rules={[
                  { required: true, message: "Please enter a Whatsapp Business Name." },
                  { pattern: /^[A-Za-z][A-Za-z0-9 ]+$/, message: "Whatsapp Business Name is invalid" },
                  { validator: this.existingNameValidator }
                ]}
              >
                <KuikaInput size="md" data-testid="whatsappBusinessSettingNameInput" autoFocus />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Account Id"
              required
              tooltipContentKey="appsettings_whatsappbusinesssettingpop_up_accountid"
            >
              <Form.Item
                name="businessAccountId"
                rules={[{ required: true, message: "Please enter a Whatsapp Business Account Id." }]}
              >
                <KuikaInput size="md" data-testid="whatsappBusinessSettingBusinessAccountIdInput" />
              </Form.Item>
            </ConfigField>
            <ConfigField label="App Id" required tooltipContentKey="appsettings_whatsappbusinesssettingpop_up_appid">
              <Form.Item name="appId" rules={[{ required: true, message: "Please enter a App Id." }]}>
                <KuikaInput size="md" data-testid="whatsappBusinessSettingAppIdInput" />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="App Secret"
              required
              tooltipContentKey="appsettings_whatsappbusinesssettingpop_up_appsecret"
            >
              <Form.Item name="appSecret" rules={[{ required: true, message: "Please enter a App Secret." }]}>
                <KuikaInput size="md" data-testid="whatsappBusinessSettingAppSecretInput" />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Access Token"
              required
              tooltipContentKey="appsettings_whatsappbusinesssettingpop_up_accesstoken"
            >
              <Form.Item name="accessToken" rules={[{ required: true, message: "Please enter a Access Token." }]}>
                <KuikaInput size="md" data-testid="whatsappBusinessSettingAccessTokenInput" />
              </Form.Item>
            </ConfigField>

            <ConfigField
              label="Phone Number Id"
              required
              tooltipContentKey="appsettings_whatsappbusinesssettingpop_up_phonenumberid"
            >
              <Form.Item name="phoneNumberId" rules={[{ required: true, message: "Please enter a Phone Number Id." }]}>
                <KuikaInput size="md" data-testid="whatsappBusinessSettingPhoneNumberIdInput" />
              </Form.Item>
            </ConfigField>

            <ConfigField
              label="Version Number"
              required
              tooltipContentKey="appsettings_whatsappbusinesssettingpop_up_versionnumber"
            >
              <Form.Item name="versionNumber" rules={[{ required: true, message: "Please enter a Version Number." }]}>
                <KuikaInput size="md" data-testid="whatsappBusinessSettingVersionNumberInput" placeholder="v21.0" />
              </Form.Item>
            </ConfigField>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
